import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LiveKitRoom, VideoConference } from "@livekit/components-react";
import "@livekit/components-styles";
import { liveApi, type LiveLesson, type RoomTokenResponse } from "../../lib/live-api";
import { LiveTutorPanel } from "./LiveTutorPanel";

function timeRemaining(start: string, durationMin: number): string {
  const endMs = new Date(start).getTime() + durationMin * 60_000;
  const remMs = endMs - Date.now();
  if (remMs <= 0) return "00:00";
  const totalSec = Math.floor(remMs / 1000);
  const mm = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const ss = String(totalSec % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

export function LiveLessonRoom(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [lesson, setLesson] = useState<LiveLesson | null>(null);
  const [tokenInfo, setTokenInfo] = useState<RoomTokenResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    void (async () => {
      if (!id) return;
      try {
        const l = await liveApi.getLesson(id);
        setLesson(l);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [id]);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  async function join(): Promise<void> {
    if (!id) return;
    try {
      const r = await liveApi.getToken(id);
      setTokenInfo(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  if (err) {
    return (
      <div className="p-8 text-red-300">
        <div className="mb-3">Error: {err}</div>
        <Link to="/live" className="underline">← Sesje live</Link>
      </div>
    );
  }
  if (!lesson) {
    return <div className="p-8 text-ink-400">Ładowanie…</div>;
  }

  // In-room view: full-screen LiveKit conference with header.
  if (tokenInfo) {
    return (
      <div className="h-full flex flex-col bg-ink-900 text-ink-200" data-lk-theme="default">
        <header className="px-4 py-2 border-b border-ink-700 flex items-center justify-between bg-ink-900 z-10">
          <Link to="/live" className="text-xs text-ink-400 hover:text-ink-200 underline">← Sesje live</Link>
          <div className="text-sm">
            <span className="text-ink-400">Pokój:</span> <code className="text-ink-200">{tokenInfo.room}</code>
            <span className="ml-3 text-ink-400">Rola:</span> <code className="text-ink-200">{tokenInfo.role}</code>
          </div>
          <div className="text-xs text-ink-400">{timeRemaining(lesson.scheduled_at, lesson.duration_min)} pozostało</div>
        </header>
        <div className="flex-1 min-h-0 flex">
          <div className={tokenInfo.role === "tutor" ? "flex-1" : "flex-1"}>
            <LiveKitRoom
              token={tokenInfo.token}
              serverUrl={tokenInfo.url}
              connect={true}
              video={true}
              audio={true}
              data-lk-theme="default"
              style={{ height: "100%" }}
            >
              <VideoConference />
            </LiveKitRoom>
          </div>
          {tokenInfo.role === "tutor" && id && (
            <aside className="w-[360px] border-l border-ink-700 bg-ink-950/60 overflow-y-auto p-3">
              <LiveTutorPanel lessonId={id} />
            </aside>
          )}
        </div>
      </div>
    );
  }

  // Pre-join view.
  void tick;
  const remaining = timeRemaining(lesson.scheduled_at, lesson.duration_min);

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 text-ink-200">
      <header className="px-6 py-3 border-b border-ink-700 flex items-center justify-between">
        <div>
          <Link to="/live" className="text-xs text-ink-400 hover:text-ink-200 underline">← Sesje live</Link>
          <div className="text-lg font-light mt-1">
            Lekcja · {new Date(lesson.scheduled_at).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" })}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-2xl font-mono tabular-nums">{remaining}</div>
          <div className="text-xs uppercase tracking-widest text-ink-400">pozostało</div>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-12 gap-0 overflow-hidden">
        <aside className="col-span-4 border-r border-ink-700 p-6 flex flex-col gap-4">
          <div className="border border-ink-700 rounded p-3 bg-ink-800/30">
            <div className="text-xs uppercase tracking-widest text-ink-400 mb-1">Tutor</div>
            <div>{lesson.tutor?.name ?? lesson.tutor?.email ?? "—"}</div>
          </div>
          <div className="border border-ink-700 rounded p-3 bg-ink-800/30">
            <div className="text-xs uppercase tracking-widest text-ink-400 mb-1">Klient</div>
            <div>{lesson.client?.name ?? lesson.client?.email ?? "—"}</div>
          </div>
          <button
            onClick={() => void join()}
            className="mt-4 px-4 py-3 rounded-lg bg-lime-500/90 hover:bg-lime-400 text-ink-900 font-medium"
          >
            Dołącz do sesji
          </button>
          <div className="text-xs text-ink-500 mt-2">
            Po kliknięciu pojawi się okno video. Sprawdź mikrofon i kamerę przed wejściem.
          </div>
        </aside>

        <main className="col-span-8 p-6 flex items-center justify-center text-ink-500">
          <div className="text-center max-w-md">
            <div className="text-sm uppercase tracking-widest mb-2">Sesja gotowa</div>
            <p className="text-ink-400">
              Tutor i klient dołączają do tego samego pokoju. Po wejściu obojga rozpoczyna się nagrywanie
              (status lekcji przechodzi z SCHEDULED → IN_PROGRESS).
            </p>
            <p className="text-ink-500 text-xs mt-4">
              Pokój: <code className="text-ink-400">{lesson.livekit_room}</code>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
